import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, List, BookPlus, Check, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import magicCardImage from '../assets/home-icons/magic-card.png';
import { useLocation, useNavigate } from 'react-router-dom';
import ScenePlayer from './ScenePlayer';
import { createSceneSelectionRequestId } from '../services/SceneSelectionRequest';
import apiClient from '../services/apiClient';
import ReviewLevelUpDialog from './ReviewLevelUpDialog';
import PracticeSession from './PracticeSession';
import MarkdownText from './MarkdownText';
import './TriumphPage.css';
import './WritingLearningPage.css';
import { t } from '../i18n';
const w = (key, replacements = {}) => t(`writingExam.${key}`, undefined, replacements);

const empty = { records: [], total: 0, page: 1, stats: { count: 0, average: 0, perfect: 0, saved: 0 } };
const reviewSettings = { practiceMode: 'quick', writingReview: true, quickTestEnabled: false };
const scoreBand = score => score >= 90 ? 'purple' : score >= 80 ? 'green' : score >= 70 ? 'teal' : score >= 60 ? 'amber' : 'red';
const historyDateFormat = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' });
const historyDate = value => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : historyDateFormat.format(date);
};
function reviewCard(item) {
  return { id: `writing~${item.id}`, front: item.Q, back: item.A, noteTitle: w('reviewTitle'), noteId: 'writing-review',
    cardType: 'normal', level: 1, score: 0, practiceCount: 0, assessmentGrade: null, testIndex: 0, writingRecord: item };
}
const writingRecordKeys = record => [record?.id && `record:${record.id}`, record?.pairId && `pair:${record.pairId}`].filter(Boolean);

export default function WritingLearningPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [scene, setScene] = useState(null);
  const [sceneLoading, setSceneLoading] = useState(false);
  const sceneRequest = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const [data, setData] = useState(empty);
  const [today, setToday] = useState(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const savingRef = useRef(null);
  const [importedKeys, setImportedKeys] = useState(() => new Set());
  const [review, setReview] = useState(null);
  const [notice, setNotice] = useState(location.state?.writingComplete ? w('complete') : '');
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [historyDay, setHistoryDay] = useState(() => historyDate(Date.now()));
  useEffect(() => {
    const updateDay = () => setHistoryDay(historyDate(Date.now()));
    const timer = setInterval(updateDay, 60000);
    document.addEventListener('visibilitychange', updateDay);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', updateDay); };
  }, []);
  const recordGroup = item => historyDate(item.gradedAt) === historyDay ? 'todayRecords' : 'earlierRecords';
  const inFlight = useRef(false);
  const refreshId = useRef(0);
  const rememberSavedRecords = useCallback(records => {
    setImportedKeys(current => {
      const next = new Set(current);
      records.filter(record => record.saved).forEach(record => writingRecordKeys(record).forEach(key => next.add(key)));
      return next.size === current.size ? current : next;
    });
  }, []);

  const refresh = useCallback(async () => {
    const id = ++refreshId.current;
    setLoading(true);
    try {
      const [history, current] = await Promise.all([apiClient.getWritingRecords({ query, filter, page }), apiClient.getWritingToday()]);
      if (id !== refreshId.current) return;
      setData(history); setToday(current); setError(''); rememberSavedRecords(history.records);
    } catch (err) { if (id === refreshId.current) setError(err.message); }
    finally { if (id === refreshId.current) setLoading(false); }
  }, [query, filter, page, rememberSavedRecords]);
  const invalidateRefresh = useCallback(() => { refreshId.current++; }, []);
  useEffect(() => { const timer = setTimeout(refresh, query ? 250 : 0); return () => { clearTimeout(timer); invalidateRefresh(); }; }, [refresh, query, invalidateRefresh]);
  const run = async callback => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(''); setNotice('');
    try { await callback(); }
    catch (err) { setError(err.message || w('failed')); }
    finally { inFlight.current = false; setBusy(false); }
  };
  const begin = async () => {
    if (sceneRequest.current || scene) return;
    sceneRequest.current = true;
    setSceneLoading(true);
    try {
      const result = await apiClient.getRandomScene({ trigger: 'levelup.before.event', requestId: createSceneSelectionRequestId(), timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      if (!mounted.current) return;
      if (result?.scene?.frames?.length) setScene(result.scene);
      else navigate('/level-up/practice');
    } catch {
      if (mounted.current) navigate('/level-up/practice');
    } finally {
      sceneRequest.current = false;
      if (mounted.current) setSceneLoading(false);
    }
  };
  const beginReview = () => run(async () => {
    const result = await apiClient.getWritingReview({ query, filter });
    if (!result.records.length) { setNotice(w('noReview')); return; }
    rememberSavedRecords(result.records);
    setReview(result.records.map(reviewCard));
  });
  const isRecordImported = record => Boolean(record?.saved) || writingRecordKeys(record).some(key => importedKeys.has(key));
  const openImport = record => {
    if (!record?.id || savingRef.current || isRecordImported(record)) return;
    savingRef.current = record;
    setSaving(record);
  };
  const savingDialog = saving && <ReviewLevelUpDialog key={saving.id} writingRecord={saving}
    card={{ id: saving.id, front: saving.Q, back: saving.A, cardType: 'normal', level: 1 }}
    onClose={() => { savingRef.current = null; setSaving(null); }}
    onCreated={() => { rememberSavedRecords([{ ...saving, saved: true }]); void refresh(); }} />;

  if (review) return <>
    <PracticeSession flashCards={review} settings={reviewSettings} onSessionComplete={() => { setReview(null); refresh(); }}
      onImportCard={card => openImport(card?.writingRecord)} isCardImported={card => isRecordImported(card?.writingRecord)}
      externalPause={Boolean(saving)} />
    {savingDialog}
  </>;

  return <main className="triumph-page writing-page">
    {scene && <ScenePlayer scene={scene} onContinue={() => navigate('/level-up/practice')} variant="coaching" nextLabel={t('common.next')} continueLabel={w('start')} />}
    <header className="triumph-page-header">
      <button type="button" className="btn-back-inline" onClick={() => navigate('/')} aria-label={t('learningNavigation.common.backHome')}><ArrowLeft size={24} aria-hidden="true" /></button>
      <div className="triumph-title-block"><h1 className="page-title-fx page-title-triumph">{w('title')}</h1><p>{w('intro')}</p></div>
    </header>
    <section className="writing-hero" aria-labelledby="writing-hero-prompt">
      <div className="writing-hero-copy">
        <h2 id="writing-hero-prompt" className="writing-hero-prompt">{w('heroPrompt')}</h2>
        <p className="writing-stats-summary" aria-label={w('stats')}>
          {w('totalSentences', { count: data.stats.count })}<span aria-hidden="true"> · </span>
          {w('recentAverage', { score: data.stats.count >= 10 && data.stats.recentAverage != null ? w('score', { score: data.stats.recentAverage }) : w('averageUnavailable') })}
        </p>
      </div>
      <div className="writing-start"><button className="writing-primary" type="button" disabled={busy || loading || sceneLoading || Boolean(scene) || (today?.exam?.complete && today?.dailyLimitEnabled !== false) || !today?.configured} onClick={begin}>
        <img className="writing-start-icon" src={magicCardImage} alt="" aria-hidden="true" /> {today?.exam?.complete ? w(today?.dailyLimitEnabled === false ? 'startAgain' : 'done') : today?.exam ? w('resume') : w('start')}
      </button></div>
    </section>
    {error && <div role="alert" className="writing-error">{error}<button type="button" onClick={refresh}>{w('retry')}</button></div>}
    {notice && <p role="status">{notice}</p>}

      <section className="triumph-board" aria-label={w('history')}>
        <div className="triumph-toolbar"><label className="triumph-search"><Search size={17} aria-hidden="true" /><input aria-label={w('search')} placeholder={w('searchHint')} value={query} onChange={event => { setQuery(event.target.value); setPage(1); }} /></label>
          <div className="writing-filters" role="group" aria-label={w('filters')}>{[['all', List], ['unsaved', BookPlus], ['saved', Check]].map(([value, Icon]) => <button type="button" key={value} aria-label={w(value)} title={w(value)} aria-pressed={filter === value} onClick={() => { setFilter(value); setPage(1); }}><Icon size={19} aria-hidden="true" /></button>)}</div>
          <button type="button" className="triumph-review-button" disabled={busy || loading || sceneLoading || Boolean(scene) || !data.total} onClick={beginReview}>{w('review')}</button>
        </div>
        {loading ? <p role="status">{w('loading')}</p> : !data.records.length ? <p className="triumph-empty-results">{w('empty')}</p> : <div className="triumph-card-list">
          {data.records.map((item, index) => <React.Fragment key={item.id}>
            {(index === 0 || recordGroup(item) !== recordGroup(data.records[index - 1])) && <h2 className="writing-history-separator">{w(recordGroup(item))}</h2>}
            <article className="triumph-card-row writing-row">
            <button type="button" className="writing-card-toggle" aria-label={`${w(expandedFeedback === item.id ? 'collapseRecord' : 'expandRecord')}: ${item.Q}`} aria-expanded={expandedFeedback === item.id} aria-controls={`writing-detail-${item.id}`} onClick={() => setExpandedFeedback(expandedFeedback === item.id ? null : item.id)} />
            <div className={`triumph-card-stamp writing-score writing-score--${scoreBand(item.result.score)}${item.result.score === 100 ? ' is-perfect' : ''}`} aria-label={w('scoreLabel', { score: item.result.score })}><strong>{item.result.score}</strong></div>
            <div className="triumph-card-copy"><div className="writing-line" aria-label={w('question')}><MarkdownText text={item.Q} /></div>
              <div className="writing-line writing-reference" lang="en" aria-label={w('reference')}><MarkdownText text={item.A} /></div>
            </div>
            <div className="triumph-card-actions"><button className="triumph-level-up-button" type="button" disabled={isRecordImported(item)} aria-label={isRecordImported(item) ? w('saved') : w('addToNote')} title={isRecordImported(item) ? w('saved') : w('saveTitle')} onClick={() => openImport(item)}>{isRecordImported(item) ? <Check size={20} /> : <BookPlus size={20} />}</button></div>
              {expandedFeedback === item.id && <div id={`writing-detail-${item.id}`} className="writing-feedback-detail" role="note">
                <div className="writing-detail-line writing-student" aria-label={w('answer')}><b>{w('answer')}</b><div className="writing-detail-text" lang="en"><MarkdownText text={item.answer} /></div></div>
                <div className="writing-detail-line" aria-label={w('explanation')}><b>{w('explanation')}</b><p className="writing-detail-text">{item.result.feedback}</p></div>
              </div>}
          </article></React.Fragment>)}
        </div>}
        <nav className="triumph-pagination" aria-label={w('pagination')}><button type="button" aria-label={w('previous')} disabled={loading || data.page <= 1} onClick={() => setPage(data.page - 1)}><ChevronLeft size={18} /></button><span>{data.page} / {Math.max(1, Math.ceil(data.total / 10))}</span><button type="button" aria-label={w('next')} disabled={loading || data.page * 10 >= data.total} onClick={() => setPage(data.page + 1)}><ChevronRight size={18} /></button></nav>
      </section>
    {savingDialog}
  </main>;
}
